import * as THREE from 'three';

export class Enemy {
  private mesh!: THREE.Group;
  private health = 100;
  private velocity = new THREE.Vector3();
  private attackCooldown = 0;
  private invincible = 0;
  private target: THREE.Vector3 = new THREE.Vector3();
  private dead = false;
  
  private readonly SPEED = 8;
  
  constructor(scene: THREE.Scene) {
    this.createMesh(scene);
  }
  
  private createMesh(scene: THREE.Scene): void {
    this.mesh = new THREE.Group();
    
    const bodyGeo = new THREE.BoxGeometry(1.2, 2, 1.2);
    const bodyMat = new THREE.MeshStandardMaterial({
      color: 0xff4444,
      metalness: 0.2,
      roughness: 0.8
    });
    const body = new THREE.Mesh(bodyGeo, bodyMat);
    body.position.y = 1.5;
    body.castShadow = true;
    this.mesh.add(body);
    
    const eyeGeo = new THREE.SphereGeometry(0.2, 8, 8);
    const eyeMat = new THREE.MeshBasicMaterial({ color: 0x000000 });
    
    const leftEye = new THREE.Mesh(eyeGeo, eyeMat);
    leftEye.position.set(-0.3, 2, 0.5);
    this.mesh.add(leftEye);
    
    const rightEye = new THREE.Mesh(eyeGeo, eyeMat);
    rightEye.position.set(0.3, 2, 0.5);
    this.mesh.add(rightEye);
    
    const pupilGeo = new THREE.SphereGeometry(0.1, 8, 8);
    const pupilMat = new THREE.MeshBasicMaterial({ color: 0xff0000 });
    
    const leftPupil = new THREE.Mesh(pupilGeo, pupilMat);
    leftPupil.position.set(-0.3, 2, 0.65);
    this.mesh.add(leftPupil);
    
    const rightPupil = new THREE.Mesh(pupilGeo, pupilMat);
    rightPupil.position.set(0.3, 2, 0.65);
    this.mesh.add(rightPupil);
    
    scene.add(this.mesh);
  }
  
  setPosition(x: number, y: number, z: number): void {
    this.mesh.position.set(x, y, z);
  }
  
  getPosition(): THREE.Vector3 {
    return this.mesh.position.clone();
  }
  
  isDead(): boolean {
    return this.dead;
  }
  
  update(delta: number, playerPos: THREE.Vector3): void {
    if (this.dead) return;
    
    this.target.copy(playerPos);
    const direction = this.target.clone().sub(this.mesh.position);
    direction.y = 0;
    
    if (direction.length() > 3) {
      direction.normalize();
      this.velocity.x = direction.x * this.SPEED;
      this.velocity.z = direction.z * this.SPEED;
      
      this.mesh.rotation.y = Math.atan2(direction.x, direction.z);
    } else {
      this.velocity.x *= 0.95;
      this.velocity.z *= 0.95;
    }
    
    this.mesh.position.x += this.velocity.x * delta;
    this.mesh.position.z += this.velocity.z * delta;
    
    this.mesh.position.x = Math.max(-90, Math.min(90, this.mesh.position.x));
    this.mesh.position.z = Math.max(-90, Math.min(90, this.mesh.position.z));
    
    const body = this.mesh.children[0] as THREE.Mesh;
    if (body) {
      body.rotation.y += delta * 2;
    }
  }
  
  tryAttack(): boolean {
    if (this.attackCooldown <= 0) {
      this.attackCooldown = 1;
      return true;
    }
    this.attackCooldown -= 0.016;
    return false;
  }
  
  takeDamage(amount: number): void {
    if (this.invincible > 0 || this.dead) return;
    
    this.health -= amount;
    this.invincible = 0.3;
    
    if (this.health <= 0) {
      this.dead = true;
      this.mesh.visible = false;
      return;
    }
    
    const body = this.mesh.children[0] as THREE.Mesh;
    if (body) {
      const mat = body.material as THREE.MeshStandardMaterial;
      mat.emissive.set(0xff0000);
      mat.emissiveIntensity = 1;
      setTimeout(() => {
        mat.emissiveIntensity = 0;
      }, 100);
    }
  }
}